import React, { useState } from 'react';
import { Alert, Button, Checkbox, Empty, Input, Select, Spin, Table, Tag, Typography, message } from 'antd';
import { apiConfig } from '../../authConfig';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useResource } from '../../hooks/useResource';
import { formatDdMmYyyy } from '../../utils/trainingModel';

const { Text, Paragraph } = Typography;

const PROVIDERS = [
    { value: 'sumtotal', label: 'SumTotal' },
    { value: 'cornerstone', label: 'Cornerstone' },
    { value: 'sap_success_factors', label: 'SAP SuccessFactors Learning' },
    { value: 'moodle', label: 'Moodle' },
    { value: 'custom_scorm', label: 'Custom / SCORM' },
];
const providerLabel = (v) => PROVIDERS.find((p) => p.value === v)?.label || v;

/**
 * Inbound LMS completion webhook configuration — project-scoped, Lead Advisor only
 * (GET/POST .../training/lms-integrations; the API 403s every other role).
 *
 * The shared secret is write-only: it is encrypted at rest and never returned, so this panel
 * only ever shows metadata. Saving a provider that already exists rotates its secret. The
 * integration id is displayed because the LMS must send it in the signed webhook payload.
 */
const LmsIntegrationsPanel = ({ projectId }) => {
    const { request } = useApiRequest();
    const [provider, setProvider] = useState(null);
    const [secret, setSecret] = useState('');
    const [saving, setSaving] = useState(false);
    const [syncEnabled, setSyncEnabled] = useState(false);
    const [toggling, setToggling] = useState(null);
    const [error, setError] = useState(null);

    const integrations = useResource(
        async () => (await request('GET', `/projects/${projectId}/training/lms-integrations`)).data.integrations || [],
        [projectId, request],
    );

    const save = async () => {
        if (!provider) return setError('Choose an LMS provider.');
        const secretLen = secret.trim().length;
        if (secretLen < 32 || secretLen > 512) return setError('The shared secret must be 32 to 512 characters.');
        setSaving(true);
        setError(null);
        try {
            const { data } = await request('POST', `/projects/${projectId}/training/lms-integrations`, {
                body: { lmsProvider: provider, sharedSecret: secret.trim(), syncEnabled },
            });
            setSecret('');
            message.success(
                data.rotated
                    ? 'Secret rotated. Update it in your LMS - completions signed with the old one are now rejected.'
                    : 'LMS integration saved. The secret is stored encrypted and cannot be viewed again.',
            );
            integrations.reload();
        } catch (err) {
            setError(err.message || 'Could not save the integration.');
        } finally {
            setSaving(false);
        }
    };

    const setSync = async (row, enabled) => {
        setToggling(row.id);
        try {
            await request('POST', `/projects/${projectId}/training/lms-integrations`, {
                body: { lmsProvider: row.lmsProvider, syncEnabled: enabled },
            });
            message.success(enabled ? 'Webhook enabled.' : 'Webhook disabled.');
            integrations.reload();
        } catch (err) {
            message.error(err.message || 'Could not change the webhook setting.');
        } finally {
            setToggling(null);
        }
    };

    const columns = [
        { title: 'Provider', dataIndex: 'lmsProvider', key: 'provider', render: (p) => providerLabel(p) },
        { title: 'Integration ID', dataIndex: 'id', key: 'id', render: (id) => <Text code copyable={{ text: id }}>{id}</Text> },
        {
            title: 'Webhook URL',
            key: 'webhook',
            render: (_, r) => {
                const url = `${apiConfig.baseUrl}/api/v1/webhooks/lms/${r.lmsProvider}/enrollment-complete`;
                return <Text code copyable={{ text: url }}>{url}</Text>;
            },
        },
        {
            title: 'Webhook',
            key: 'sync',
            width: 190,
            render: (_, r) => (
                <div>
                    <Tag color={r.syncEnabled ? 'green' : 'default'}>{r.syncEnabled ? 'Accepting completions' : 'Off - rejecting all'}</Tag>
                    <Button size="small" loading={toggling === r.id} onClick={() => setSync(r, !r.syncEnabled)}>
                        {r.syncEnabled ? 'Disable' : 'Enable'}
                    </Button>
                </div>
            ),
        },
        { title: 'Configured', dataIndex: 'createdAt', key: 'created', width: 120, render: (d) => formatDdMmYyyy(d) },
    ];

    return (
        <div>
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Reflect completions from your organisation's LMS"
                description="Your LMS signs each completion webhook (header X-Lms-Signature, HMAC over the raw body) with the shared secret below and includes this integration's ID (`integrationId`) and the learner's enrolment ID (`enrollmentId`, shown in Training history), plus `completedAt` and, for a course with a quiz, `scoreJson.answers` (one option index per question). An unsigned or mis-signed call is rejected."
            />
            {integrations.status === 'loading' && !integrations.data && <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>}
            {integrations.status === 'error' && (
                <Alert
                    type="error"
                    showIcon
                    message="Could not load LMS integrations"
                    description={integrations.error?.message}
                    action={<Button size="small" onClick={integrations.reload}>Retry</Button>}
                    style={{ marginBottom: 16 }}
                />
            )}
            {integrations.status === 'ready' && (integrations.data || []).length === 0 && (
                <Empty description="No LMS integration configured." style={{ marginBottom: 16 }} />
            )}
            {(integrations.data || []).length > 0 && (
                <Table rowKey={(r) => r.id} columns={columns} dataSource={integrations.data} pagination={false} style={{ marginBottom: 24 }} />
            )}

            <Text strong>Add or rotate an integration</Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 440, marginTop: 8 }}>
                <Select aria-label="LMS provider" placeholder="LMS provider" value={provider} onChange={setProvider} options={PROVIDERS} />
                <Input.Password aria-label="Shared secret" placeholder="Shared secret, 32-512 characters (write-only)" value={secret} onChange={(e) => setSecret(e.target.value)} autoComplete="new-password" />
                <Checkbox checked={syncEnabled} onChange={(e) => setSyncEnabled(e.target.checked)}>Accept completion webhooks now</Checkbox>
                <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
                    The webhook is off by default: while it is off, every delivery from your LMS is rejected exactly like a bad signature. Only the person who configured an integration (or a platform administrator) can enable it or rotate its secret; any Lead Advisor can disable it.
                </Paragraph>
                <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
                    Saving a provider that is already configured replaces its secret (audited as a rotation; the secret itself is never logged). Only the Lead Advisor who created an integration can rotate it.
                </Paragraph>
                {error && <Text type="danger" role="alert">{error}</Text>}
                <div>
                    <Button type="primary" loading={saving} onClick={save}>Save integration</Button>
                </div>
            </div>
        </div>
    );
};

export default LmsIntegrationsPanel;
